<template>
  <div v-if="visible" class="modal-overlay">
    <LoadingOverlay :visible="cargando" message="Cargando estructura del modelo…" />
    <div class="modal-contenido" :class="{ 'modal-grande': paso === 2 }">
      <h4 class="modal-titulo">Clonar Modelo</h4>

      <!-- PASO 1: elegir tipo de clonación -->
      <template v-if="paso === 1">
        <p class="mensaje">¿Cómo desea realizar la clonación?</p>

        <p class="nota-clonacion">
          El clon se crea como plantilla: copia la estructura, pero <strong>no</strong>
          se genera una versión ni se copian indicadores. La versión se crea después
          con «Versionar».
        </p>

        <div class="opciones-lista">
          <label class="opcion-card" :class="{ seleccionada: opcion === 'completo' }">
            <input type="radio" v-model="opcion" value="completo" />
            <div class="opcion-texto">
              <strong>Clonar modelo completo</strong>
              <span class="opcion-desc">Se copia toda la estructura del modelo.</span>
            </div>
          </label>

          <label class="opcion-card" :class="{ seleccionada: opcion === 'seleccion' }">
            <input type="radio" v-model="opcion" value="seleccion" />
            <div class="opcion-texto">
              <strong>Escoger qué clonar</strong>
              <span class="opcion-desc">
                Verá el modelo completo y podrá pulsar la
                <span class="x-ejemplo">✕</span>
                de los elementos que <em>no</em> desea incluir.
              </span>
            </div>
          </label>
        </div>

        <div class="form-grupo-nombre">
          <label class="cambiar-nombre-label">
            <input type="checkbox" v-model="cambiarNombre" class="cambiar-nombre-check" />
            ¿Desea cambiar el nombre del modelo?
          </label>
          <input
            v-if="cambiarNombre"
            v-model="nuevoNombre"
            type="text"
            class="form-input"
            placeholder="Nuevo nombre del modelo"
          />
        </div>

        <div class="botones">
          <button class="btn salir" @click.stop="$emit('cancelar')">Cancelar</button>
          <button class="btn guardar" :disabled="!opcion" @click.stop="avanzar">
            {{ opcion === 'completo' ? 'Clonar' : 'Siguiente ' }}
          </button>
        </div>
      </template>

      <!-- PASO 2: árbol de selección -->
      <template v-else>
        <SelectorEstructuraArbol
          ref="selector"
          :idModelo="idModelo"
          @update:cargando="cargando = $event"
        />

        <div class="botones">
          <button class="btn salir" @click.stop="paso = 1">Volver</button>
          <button class="btn guardar" :disabled="cargando" @click.stop="confirmar">
            Confirmar Clonación
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Clonación de un modelo.
 *
 * Copia SOLO la estructura, con filtro opcional por ordinal. El clon NO nace con
 * versión y, por tanto, no hereda indicadores: viven en la versión.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, watch } from 'vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import SelectorEstructuraArbol from '@/components/SelectorEstructuraArbol.vue'

const props = defineProps<{
  visible: boolean
  idModelo: number | null
  idVersionOrigen: number | null
}>()

const emit = defineEmits<{
  (e: 'aceptar', payload: { opcion: string; ordinales?: string[]; nuevoNombre?: string }): void
  (e: 'cancelar'): void
}>()

const opcion = ref<'completo' | 'seleccion' | ''>('')
const paso = ref<1 | 2>(1)
const cargando = ref(false)
const cambiarNombre = ref(false)
const nuevoNombre = ref('')
const selector = ref<InstanceType<typeof SelectorEstructuraArbol> | null>(null)

watch(() => props.visible, (v) => {
  if (v) {
    opcion.value = ''
    paso.value = 1
    cargando.value = false
    cambiarNombre.value = false
    nuevoNombre.value = ''
  }
})

function avanzar() {
  if (!opcion.value) return
  const nombreFinal = cambiarNombre.value && nuevoNombre.value.trim() ? nuevoNombre.value.trim() : undefined
  if (opcion.value === 'completo') {
    emit('aceptar', { opcion: 'completo', nuevoNombre: nombreFinal })
  } else {
    paso.value = 2
  }
}

function confirmar() {
  const nombreFinal = cambiarNombre.value && nuevoNombre.value.trim() ? nuevoNombre.value.trim() : undefined
  const ordinalesCalculados = selector.value?.obtenerOrdinales() ?? []
  emit('aceptar', { opcion: 'seleccion', ordinales: ordinalesCalculados, nuevoNombre: nombreFinal })
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal-contenido {
  background: white;
  padding: 1.75rem;
  border-radius: 12px;
  width: 480px;
  max-width: 96vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  font-family: var(--font-family);
}

.modal-grande {
  width: 780px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
}

.modal-titulo {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.mensaje {
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  color: #333;
}

.nota-clonacion {
  margin-bottom: 1.25rem;
  padding: 0.6rem 0.75rem;
  border-left: 3px solid #eea222;
  background-color: #fdf6e7;
  border-radius: 4px;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #5c4a1e;
}

.form-grupo-nombre {
  margin-bottom: 1.25rem;
}

.cambiar-nombre-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.cambiar-nombre-check {
  width: auto;
  height: auto;
  accent-color: #0e69a0;
  margin: 0;
  cursor: pointer;
}

.form-input {
  width: 100%;
  padding: 0.55rem 0.85rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.9rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

/* Paso 1 */
.opciones-lista {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.opcion-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.opcion-card:hover {
  border-color: #0e69a0;
  background: #f5f9fc;
}

.opcion-card.seleccionada {
  border-color: #0e69a0;
  background: #eaf4fb;
}

.opcion-card input[type="radio"] {
  margin-top: 3px;
  accent-color: #0e69a0;
  flex-shrink: 0;
}

.opcion-texto {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.opcion-texto strong {
  font-size: 0.95rem;
  color: #222;
}

.opcion-desc {
  font-size: 0.82rem;
  color: #666;
  line-height: 1.4;
}

.x-ejemplo {
  display: inline-block;
  color: #cc2200;
  font-weight: 800;
  transform: rotate(-12deg);
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;
  flex-shrink: 0;
}

.btn {
  padding: 0.6rem 1.3rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: var(--font-family);
  font-size: 0.9rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.salir {
  background-color: white;
  border: 1px solid #ccc;
  color: #333;
}

.btn.salir:hover:not(:disabled) {
  background-color: #d41a1a;
  color: white;
}

.btn.guardar {
  background-color: #eea222;
  color: black;
}

.btn.guardar:hover:not(:disabled) {
  background-color: #0e69a0;
  color: white;
}
</style>
